import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './config';

// ============================================
// COLLECTION REFERENCES
// ============================================

export const usersRef = collection(db, 'users');
export const rafflesRef = collection(db, 'raffles');
export const entriesRef = collection(db, 'entries');
export const handoversRef = collection(db, 'handovers');
export const reviewsRef = collection(db, 'reviews');
export const disputesRef = collection(db, 'disputes');
export const messagesRef = collection(db, 'messages');
export const transactionsRef = collection(db, 'transactions');
export const skillQuestionsRef = collection(db, 'skillQuestions');

// ============================================
// USER PROFILE MANAGEMENT
// ============================================

/**
 * Create a new user profile in Firestore
 * Called during signup
 */
export async function createUserProfile(userId, profileData) {
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, {
    ...profileData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get user profile by ID
 */
export async function getUserProfile(userId) {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return { id: userSnap.id, ...userSnap.data() };
  }
  return null;
}

/**
 * Update user profile fields
 */
export async function updateUserProfile(userId, updates) {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Subscribe to user profile changes
 */
export function subscribeToUserProfile(userId, callback) {
  const userRef = doc(db, 'users', userId);
  return onSnapshot(userRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback(null);
    }
  });
}

/**
 * Check if phone number is already registered
 */
export async function isPhoneNumberTaken(phoneNumber) {
  const q = query(usersRef, where('phone', '==', phoneNumber), limit(1));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Update user stats (increment counters)
 */
export async function updateUserStats(userId, statUpdates) {
  const userRef = doc(db, 'users', userId);
  const updates = {};

  Object.entries(statUpdates).forEach(([key, value]) => {
    updates[`stats.${key}`] = increment(value);
  });

  await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// GENERIC CRUD OPERATIONS
// ============================================

/**
 * Get document reference by collection and ID
 */
export function getDocRef(collectionName, docId) {
  return doc(db, collectionName, docId);
}

/**
 * Get a single document by ID
 */
export async function getDocument(collectionName, docId) {
  const docRef = doc(db, collectionName, docId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
}

/**
 * Create a new document with auto-generated ID
 */
export async function createDocument(collectionName, data) {
  const colRef = collection(db, collectionName);
  const docRef = await addDoc(colRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Create a document with a specific ID
 */
export async function createDocumentWithId(collectionName, docId, data) {
  const docRef = doc(db, collectionName, docId);
  await setDoc(docRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docId;
}

/**
 * Update an existing document
 */
export async function updateDocument(collectionName, docId, data) {
  const docRef = doc(db, collectionName, docId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a document
 */
export async function deleteDocument(collectionName, docId) {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
}

/**
 * Subscribe to document changes (real-time)
 */
export function subscribeToDocument(collectionName, docId, callback, onError) {
  const docRef = doc(db, collectionName, docId);
  return onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        callback({ id: docSnap.id, ...docSnap.data() });
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error(`Firestore subscription error (${collectionName}/${docId}):`, error);
      if (onError) onError(error);
    }
  );
}

/**
 * Subscribe to collection query changes (real-time)
 */
export function subscribeToQuery(queryRef, callback, onError) {
  return onSnapshot(
    queryRef,
    (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(results);
    },
    (error) => {
      console.error('Firestore query subscription error:', error);
      if (onError) onError(error);
    }
  );
}

/**
 * Execute a query and return results
 */
export async function executeQuery(queryRef) {
  const snapshot = await getDocs(queryRef);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Query documents with conditions
 *
 * @param collectionName - The collection to query
 * @param conditions - Array of [field, operator, value] tuples
 * @param options - { orderBy: [[field, direction]], limit: number }
 * @returns Array of documents
 *
 * @example
 * queryDocuments('raffles', [['sellerId', '==', 'user123'], ['status', '==', 'active']], { orderBy: [['createdAt', 'desc']], limit: 10 })
 */
export async function queryDocuments(collectionName, conditions = [], options = {}) {
  const colRef = collection(db, collectionName);
  const constraints = [];

  // Add where clauses
  for (const [field, operator, value] of conditions) {
    constraints.push(where(field, operator, value));
  }

  // Add orderBy clauses
  if (options.orderBy) {
    for (const [field, direction] of options.orderBy) {
      constraints.push(orderBy(field, direction || 'asc'));
    }
  }

  // Add limit
  if (options.limit) {
    constraints.push(limit(options.limit));
  }

  const q = query(colRef, ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

// ============================================
// MESSAGING
// ============================================

/**
 * Subscribe to messages for a raffle thread
 * Messages are ordered by createdAt ascending (oldest first)
 *
 * Note: Query must include participants constraint for security rules
 */
export function subscribeToMessages(raffleId, userId, callback, onError) {
  if (!userId) {
    if (onError) onError(new Error('User ID required for messages'));
    return () => {};
  }

  const q = query(
    messagesRef,
    where('raffleId', '==', raffleId),
    where('participants', 'array-contains', userId),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(messages);
    },
    (error) => {
      console.error('Messages subscription error:', error);
      if (onError) onError(error);
    }
  );
}

/**
 * Send a message in a raffle thread
 */
export async function sendMessage(raffleId, senderId, receiverId, text) {
  const messageId = await createDocument('messages', {
    raffleId,
    senderId,
    receiverId,
    participants: [senderId, receiverId], // For security rules
    text: text.trim(),
    readAt: null,
  });
  return messageId;
}

/**
 * Mark messages as read
 */
export async function markMessagesAsRead(messageIds) {
  const batch = [];
  for (const messageId of messageIds) {
    batch.push(updateDocument('messages', messageId, { readAt: serverTimestamp() }));
  }
  await Promise.all(batch);
}

// ============================================
// REVIEWS
// ============================================

/**
 * Create a review
 */
export async function createReview(reviewData) {
  return createDocument('reviews', reviewData);
}

/**
 * Get reviews for a user (as reviewee)
 */
export async function getUserReviews(userId, limitCount = 10) {
  const q = query(
    reviewsRef,
    where('revieweeId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  return executeQuery(q);
}

/**
 * Check if user has already reviewed for a specific raffle
 */
export async function hasUserReviewed(raffleId, reviewerId) {
  const q = query(
    reviewsRef,
    where('raffleId', '==', raffleId),
    where('reviewerId', '==', reviewerId),
    limit(1)
  );
  const results = await executeQuery(q);
  return results.length > 0;
}

// ============================================
// QUERY HELPERS (re-exported)
// ============================================

export {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
};
